import { Injectable } from '@angular/core';

import { IOption } from '@shared/models/icommons.model';
import { AppStorageService } from '@shared/services/app-storage.service';
import { LookupRefService } from '@shared/services/lookup-ref.service';
import { Observable, of } from 'rxjs';
import { map, share, take, tap } from 'rxjs/operators';
import { CURRENCY_SYMBOL_LS_TOKEN, EXPIRY_TOKEN } from './symbols.constants';
import CurrencySymbolStore from './symbols.store';

export interface CurrencySymbolMap {
  [key: string]: string;
}

/**
 * Service loading a K,V of currency name as key and symbol as value at construction time,
 * saving response in localStorage, it will fetch fresh LOVs only if currencySymbolsMap token does not exist
 * in localStorage OR if __expiry value already passed 3 hours (this can be extended/reduced as required)
 */
@Injectable({ providedIn: 'root' })
export class CurrencySymbolsResolver {
  public static CurrencySymbolToken = CURRENCY_SYMBOL_LS_TOKEN;

  private symbols$: Observable<IOption[]>;

  get currencySymbolsMap$(): Observable<CurrencySymbolMap> {
    return this.symbols$.pipe(
      map(currencySymbols => currencySymbols.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {})),
      tap(currencySymbols => {
        // update singleton store
        CurrencySymbolStore.set(currencySymbols);
        // add expiry
        const now = new Date().getTime();
        const _3Hours = 1.08e7;
        const expiry = now + _3Hours;
        this.storage.setItem(CURRENCY_SYMBOL_LS_TOKEN, {
          ...currencySymbols,
          [EXPIRY_TOKEN]: expiry,
        });
      })
    );
  }

  constructor(
    private lookupService: LookupRefService,
    private storage: AppStorageService
  ) {
    this.symbols$ = this.lookupService.getCurrencySymbols().pipe(share());
  }

  public resolve() {
    const currentSymbols: {} = this.storage.getItem(CURRENCY_SYMBOL_LS_TOKEN);
    if (currentSymbols != null) {
      const now = new Date();
      // compare expiry time
      if (
        now.getTime() > currentSymbols[EXPIRY_TOKEN] ||
        // in case we only have the expiry key (edge case)
        Object.keys(currentSymbols).length <= 1
      ) {
        // If expired, reload
        return this.loadSymbols();
      }
      return of(currentSymbols);
    } else {
      // load symbols
      return this.loadSymbols();
    }
  }

  private loadSymbols() {
    return this.currencySymbolsMap$.pipe(take(1));
  }

  public getSymbol(value: string) {
    return CurrencySymbolStore.getSymbol(value);
  }
}
